import React, { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { uploadCroppedFoto, proxyImageAlsBlobUrl } from '../dokumenteStorage';
import './FotoCropperModal.css';

async function getCroppedImg(imageSrc, pixelCrop) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = 'anonymous';
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = imageSrc;
  });
  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
}

export default function FotoCropperModal({ bildUrl, neuesFile, onFertig, onAbbrechen }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  // For Firebase Storage URLs, we proxy through backend to get a blob URL for canvas
  const [canvasBildUrl, setCanvasBildUrl] = useState(null);

  useEffect(() => {
    if (!bildUrl) { setCanvasBildUrl(null); return; }
    // Blob URLs and emulator URLs (http://127.0.0.1:9199) work directly for canvas
    if (!bildUrl.startsWith('https://firebasestorage.googleapis.com/')) {
      setCanvasBildUrl(bildUrl);
      return;
    }
    // Production Firebase Storage URLs need proxying to avoid canvas CORS taint
    let revoke = null;
    proxyImageAlsBlobUrl(bildUrl).then(blobUrl => {
      revoke = blobUrl;
      setCanvasBildUrl(blobUrl);
    }).catch(() => setCanvasBildUrl(bildUrl));
    return () => { if (revoke?.startsWith('blob:')) URL.revokeObjectURL(revoke); };
  }, [bildUrl]);

  const onCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleUebernehmen = async () => {
    if (!croppedAreaPixels || !canvasBildUrl) return;
    setBusy(true);
    setFehler('');
    try {
      const blob = await getCroppedImg(canvasBildUrl, croppedAreaPixels);
      const { url, storagePath } = await uploadCroppedFoto(blob, neuesFile?.name);
      setBusy(false);
      onFertig({ url, storagePath });
    } catch (e) {
      setFehler('Upload fehlgeschlagen. Bitte versuche es erneut.');
      setBusy(false);
    }
  };

  return (
    <div className="fcrop-overlay">
      <div className="fcrop-modal">
        <div className="fcrop-header">
          <span className="fcrop-title">Foto zuschneiden</span>
          <span className="fcrop-hint">Ziehe das Bild in die gewünschte Position</span>
        </div>

        <div className="fcrop-canvas-area">
          <Cropper
            image={canvasBildUrl || bildUrl}
            crop={crop}
            zoom={zoom}
            aspect={3 / 4}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            showGrid={false}
          />
        </div>

        <div className="fcrop-zoom-row">
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={e => setZoom(Number(e.target.value))}
            className="fcrop-zoom-slider"
            aria-label="Zoom"
          />
        </div>

        {fehler && <p className="fcrop-error">{fehler}</p>}

        <div className="fcrop-actions">
          <button className="fcrop-btn-cancel" onClick={onAbbrechen} disabled={busy}>
            Abbrechen
          </button>
          <button className="fcrop-btn-confirm" onClick={handleUebernehmen} disabled={busy || !canvasBildUrl}>
            {busy ? 'Wird gespeichert…' : !canvasBildUrl ? 'Lädt…' : 'Übernehmen'}
          </button>
        </div>
      </div>
    </div>
  );
}
